const TOKEN_KEY = "tallystream.web.token";
const USER_KEY  = "tallystream.web.user";

export type Role = "viewer" | "analyst" | "admin";

export type SyncSummary = {
  totalRuns: number;
  completedRuns: number;
  failedRuns: number;
  running: boolean;
  lastCompletedAt: string | null;
  lastFailedAt: string | null;
  lastError: string | null;
};

export type AuthSession = {
  token: string;
  expiresAt: string;
  userId: string;
  role: Role;
  authType: "dhis2" | "jwt";
  workspaceId?: string;
  workspaceSlug?: string;
  workspaceName?: string;
  organizationName?: string;
  displayName?: string;
  username?: string;
  email?: string;
};

export type WorkspaceContext = {
  workspace: {
    id: string;
    tenantId?: string;
    slug?: string;
    organizationName: string;
    workspaceName: string;
    status: string;
    onboardingStage?: string;
    dhis2BaseUrl?: string;
    region?: string;
    supportTier?: string;
  };
  subscription: SubscriptionRecord | null;
  plan: SubscriptionPackage | null;
  subscriptionActive: boolean;
};

export type WorkspaceDhisInstance = {
  environment: string;
  label: string;
  serviceName: string;
  baseUrl: string;
  environmentType: "production" | "staging" | "demo" | "development";
  enabled: boolean;
  authMode: "none" | "token" | "basic" | "dhis2-session";
  lastSuccessfulConnection?: string;
  workspaceId: string | null;
  secretConfigured: boolean;
  subscriptionAvailable: boolean;
};

/* ---------- Session helpers ---------- */
export function loadSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    if (new Date(parsed.expiresAt).getTime() < Date.now()) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: AuthSession, display?: string): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(session));
  if (display) localStorage.setItem(USER_KEY, display);
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem("tallystream.web.selectedInstance");
}

export function displayName(): string {
  const session = loadSession();
  return localStorage.getItem(USER_KEY) ?? session?.displayName ?? session?.username ?? session?.userId ?? "guest";
}

export const PLATFORM_ADMIN_WORKSPACE_SLUG = "platform";

export function isPlatformAdmin(session: AuthSession | null): boolean {
  if (!session) return false;
  return session.role === "admin" && session.workspaceSlug === PLATFORM_ADMIN_WORKSPACE_SLUG;
}

export function postLoginRedirect(session: AuthSession): string {
  return isPlatformAdmin(session) ? "/admin" : "/app";
}

const SELECTED_INSTANCE_KEY = "tallystream.web.selectedInstance";

export function loadSelectedInstanceEnvironment(): string | null {
  return localStorage.getItem(SELECTED_INSTANCE_KEY);
}

export function saveSelectedInstanceEnvironment(environment: string): void {
  localStorage.setItem(SELECTED_INSTANCE_KEY, environment);
}

export function clearSelectedInstanceEnvironment(): void {
  localStorage.removeItem(SELECTED_INSTANCE_KEY);
}

/* ---------- Domain types ---------- */

export type ServiceProfile = {
  serviceName: string;
  environment: string;
  baseUrl: string;
  apiVersion: string;
  healthEndpoint: string;
  authMode: "none" | "token" | "basic" | "dhis2-session";
  requestTimeoutMs: number;
  retryCount: number;
  enabled: boolean;
  notes: string;
  lastSuccessfulConnection?: string;
  secretRef?: string;
  secretConfigured?: boolean;
  // Tenant binding. Required at create time when a platform admin
  // creates a profile on behalf of a workspace; auto-stamped from auth
  // for workspace-admin creates. Server enforces tenant isolation.
  workspaceId?: string;
  label?: string;
  environmentType?: "production" | "staging" | "demo" | "development";
};

export type AppSettings = {
  backendBaseUrl: string;
  dhis2BaseUrl: string;
  warehouseProvider: "clickhouse";
  defaultOrgUnitId: string;
  environmentName: string;
  activeServiceProfile: string;
  serviceProfiles: ServiceProfile[];
  previousServiceProfiles?: ServiceProfile[];
  programStates: Record<string, { enabled: boolean }>;
  reportDefaults: { defaultProgramId: string; defaultIndicatorId: string; defaultPeriodId: string };
  warehouseQueryLimit: number;
  aggregateIngestionEndpoint?: string;
  aggregateMetadataEndpoint?: string;
  rollupRefreshMode?: "incremental" | "scheduled" | "manual";
  rollupStrictness?: "strict" | "permissive";
  aggregateValidationStrictness?: "strict" | "warning";
  periodRollupMode?: "monthly-to-quarterly-yearly" | "flat";
  orgUnitRollupMode?: "hierarchy" | "flat";
  roleMode: "strict" | "permissive";
  startupModule: string;
  [k: string]: unknown;
};

export type OverviewSnapshot = {
  metadataProgramCount?: number;
  metadataDimensionCount?: number;
  metadataTallyCount?: number;
  metadataIndicatorCount?: number;
  replayJobsQueued?: number;
  replayJobsRunning?: number;
  replayJobsFailed?: number;
  [k: string]: unknown;
};

export type SaaSOverview = {
  workspaceCount: number;
  activeWorkspaceCount: number;
  datasetCount: number;
  activePortalCount: number;
  configuredSecretCount: number;
  restrictedSecretCount: number;
  onboardingStage: string;
  subscriptionStatus: string;
};

export type TenantWorkspace = {
  id: string;
  name: string;
  slug?: string;
  status?: string;
  createdAt?: string;
  ownerUserId?: string;
  memberCount?: number;
  [k: string]: unknown;
};

export type WorkspaceMember = {
  id: string;
  userId?: string;
  workspaceId?: string;
  role?: Role;
  status?: string;
  email?: string;
  name?: string;
  [k: string]: unknown;
};

export type SubscriptionPackage = {
  id: string;
  name: string;
  price: number;
  waived: boolean;
  features: Record<string, boolean>;
  rateLimits: Record<string, number>;
  description?: string;
};

export type SubscriptionRecord = {
  id: string;
  workspaceId: string;
  planId: string;
  status: "trialing" | "active" | "grace-period" | "past-due";
  billingEmail: string;
  renewalDate: string;
  paymentMethodLabel: string;
  entitlements: { id: string; label: string; status: string; limit?: number; used?: number }[];
  usage: { seats: number; datasets: number; apiCallsThisMonth: number };
};

export type DatasetDefinition = {
  id: string;
  name: string;
  code: string;
  datasetType: string;
  sourceDomain: string;
  owner: string;
  status: "draft" | "active" | "archived";
  sensitivity: "low" | "moderate" | "restricted";
  rowCount: number;
  lastRefreshedAt: string;
  dimensions: string[];
  measures: string[];
  tags: string[];
  publicationEligible: boolean;
  forwardingEligible: boolean;
};

export type AggregateDomainSourceBinding = {
  id: string;
  sourceClass: "tracker" | "warehouse" | "submitted-aggregate";
  programIds?: string[];
  metricIds?: string[];
  dataSetIds?: string[];
  dataElementIds?: string[];
  categoryOptionComboIds?: string[];
  attributeOptionComboIds?: string[];
};

export type AggregateDomainTargetBinding = {
  id: string;
  targetType: "report" | "publication" | "warehouse" | "dhis2-analytics";
  targetId: string;
  enabled: boolean;
};

export type AggregateDomainRule = {
  id: string;
  ruleType:
    | "simple-sum"
    | "period-rollup"
    | "org-rollup"
    | "category-grouping"
    | "attribute-grouping"
    | "weighted"
    | "hybrid-comparison";
  config: Record<string, string | number | boolean>;
};

export type AggregateDomainValidationRule = {
  id: string;
  name: string;
  severity: "warning" | "error";
  ruleType: "completeness" | "metadata-compatibility" | "period-compatibility" | "org-compatibility" | "category-compatibility";
  config: Record<string, string | number | boolean>;
};

export type AggregateDomainMetricBinding = {
  id: string;
  metricType: "tracker-tally" | "indicator" | "submitted-aggregate" | "warehouse-metric";
  sourceId: string;
  aggregationOperator: "sum" | "avg" | "latest" | "weighted";
};

export type AggregateDomain = {
  id: string;
  name: string;
  description: string;
  domainType:
    | "dataset-aligned"
    | "data-element-group"
    | "reporting"
    | "publication"
    | "hybrid-tracker-to-aggregate"
    | "submitted-aggregate-live-rollup";
  sourceClasses: Array<"tracker" | "warehouse" | "submitted-aggregate">;
  active: boolean;
  archived: boolean;
  liveRollupEnabled: boolean;
  version: number;
  sourceBindings: AggregateDomainSourceBinding[];
  targetBindings: AggregateDomainTargetBinding[];
  rules: AggregateDomainRule[];
  validationRules: AggregateDomainValidationRule[];
  metricBindings: AggregateDomainMetricBinding[];
  publicationProfiles: string[];
  dependencyIds: string[];
  createdAt: string;
  updatedAt: string;
};

export type AggregateDomainReconciliationRun = {
  id: string;
  startedAt: string;
  completedAt?: string;
  status: "running" | "completed" | "failed";
  sourceFactVarianceCount: number;
  trackerVarianceCount: number;
  metadataIssueCount: number;
};

export type AggregateRollupRun = {
  id: string;
  domainId: string;
  mode: "incremental" | "full-rebuild";
  startedAt: string;
  completedAt?: string;
  status: "running" | "completed" | "failed";
  affectedFactCount: number;
  producedRollupCount: number;
};

export type AggregateRollupIntegrityIssue = {
  domainId: string;
  rollupFactId: string;
  rollupValue: number;
  lineageSum: number;
  difference: number;
  sourceFactCount: number;
  lineageRowCount: number;
  status: "match" | "mismatch" | "orphaned-lineage" | "missing-rollup";
};

export type AggregateRollupIntegrityReport = {
  checkedAt: string;
  domainIds: string[];
  totalRollupFacts: number;
  totalLineageRows: number;
  matchCount: number;
  mismatchCount: number;
  orphanedLineageCount: number;
  results: AggregateRollupIntegrityIssue[];
};

export type AggregatePublicationProfile = {
  id: string;
  name: string;
  targetType: "dhis2" | "csv-export" | "external-api";
  domainIds: string[];
  endpoint: string;
  authMode: "none" | "token" | "basic" | "dhis2-session";
  enabled: boolean;
};

export type AggregatePublicationDataValue = {
  dataElement: string;
  period: string;
  orgUnit: string;
  categoryOptionCombo: string;
  attributeOptionCombo: string;
  value: string;
  dataSet?: string;
};

export type AggregatePublicationImportSummary = {
  imported: number;
  updated: number;
  ignored: number;
  deleted: number;
  conflicts: Array<{ object?: string; value: string }>;
};

export type AggregatePublicationItem = {
  id: string;
  publicationRunId: string;
  domainId: string;
  rollupFactId: string;
  status: "pending" | "published" | "failed" | "skipped";
  message?: string;
  dataValue?: AggregatePublicationDataValue;
  validationIssue?: string;
};

export type AggregatePublicationRun = {
  id: string;
  profileId: string;
  startedAt: string;
  completedAt?: string;
  status: "running" | "completed" | "failed" | "preview";
  itemCount: number;
  mode?: "preview" | "publish";
  idempotencyKey?: string;
  importSummary?: AggregatePublicationImportSummary;
  error?: string;
};

export type AggregatePublicationPreview = {
  profileId: string;
  generatedAt: string;
  totalCandidates: number;
  validCount: number;
  invalidCount: number;
  payload: AggregatePublicationDataValue[];
  issues: Array<{ rollupFactId: string; reason: string }>;
};

export type PublicPortalSite = {
  id: string;
  workspaceId: string;
  name: string;
  slug: string;
  visibility: "public" | "restricted";
  status: "draft" | "active";
  authMode: "none" | "password" | "invite-only";
  audience: string;
  datasetIds: string[];
  updatedAt: string;
};

export type SecretStatus = {
  id: string;
  label: string;
  kind: "dhis2-pat" | "dhis2-basic" | "webhook-signing" | "forwarding-token" | "portal-access-key";
  storage: "server-side-vault" | "dhis2-route";
  status: "configured" | "missing" | "rotating";
  exposure: "server-only";
  lastValidatedAt?: string;
};

export type DhisAppClient = {
  clientId: string;
  workspaceId: string;
  label: string;
  description?: string;
  dhis2BaseUrl?: string;
  allowedAuthorities: string[];
  status: "active" | "revoked";
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string;
  secretLastRotatedAt: string;
};

export type DhisAppClientCreateRequest = {
  workspaceId: string;
  label: string;
  description?: string;
  dhis2BaseUrl?: string;
  allowedAuthorities?: string[];
};

export type DhisAppClientCreateResponse = {
  client: DhisAppClient;
  clientSecret: string;
};

export type ReplayJob = {
  id: string;
  type?: string;
  status?: "queued" | "running" | "succeeded" | "failed" | string;
  createdAt?: string;
  updatedAt?: string;
  progress?: number;
  [k: string]: unknown;
};

export type PaymentIntentResponse = {
  status: string;
  providerReference?: string;
  redirectUrl?: string;
  [k: string]: unknown;
};

export type DhisMe = {
  id?: string;
  username?: string;
  name?: string;
  email?: string;
  authorities?: string[];
  [k: string]: unknown;
};

/* ---------- HTTP plumbing ---------- */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function downloadCsv(path: string, filename: string): Promise<void> {
  const headers = new Headers();
  const session = loadSession();
  if (session) headers.set("authorization", `Bearer ${session.token}`);
  const res = await fetch(path, { headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(text || `Download failed (${res.status})`, res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function request<T>(path: string, init: RequestInit = {}, auth: boolean = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has("content-type") && init.body) headers.set("content-type", "application/json");
  if (auth) {
    const session = loadSession();
    if (session) headers.set("authorization", `Bearer ${session.token}`);
  }
  const res = await fetch(path, { ...init, headers });
  const ct = res.headers.get("content-type") ?? "";
  const raw = ct.includes("application/json") ? await res.json().catch(() => ({})) : await res.text();
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    let code: string | undefined;
    if (raw && typeof raw === "object") {
      if ("message" in raw) msg = String((raw as { message: unknown }).message);
      if ("errorCode" in raw) code = String((raw as { errorCode: unknown }).errorCode);
    } else if (typeof raw === "string" && raw) {
      msg = raw;
    }
    throw new ApiError(msg, res.status, code);
  }
  return raw as T;
}

/* ---------- Auth ---------- */
export async function loginTenant(input: {
  workspace?: string;
  username: string;
  password: string;
}): Promise<AuthSession> {
  const payload: Record<string, string> = {
    username: input.username,
    password: input.password
  };
  if (input.workspace && input.workspace.trim()) payload.workspace = input.workspace.trim();
  const session = await request<AuthSession>(
    "/api/auth/login",
    { method: "POST", body: JSON.stringify(payload) },
    false
  );
  saveSession(session, session.displayName ?? session.username ?? input.username);
  return session;
}

export type PlanLimits = {
  seats: number;
  datasets: number;
  dhis2Instances: number;
  publicPortals: number;
  scheduledJobs: number;
  reports: number;
  scheduledReports: number;
  externalIntegrations: number;
  apiCallsPerMonth: number;
  dataRetentionDays: number;
  warehouseQueryRowsPerRun: number;
  storageGb: number;
};

export type PlanCapabilities = {
  warehouseExplorer: boolean;
  monitoringUi: boolean;
  scheduling: boolean;
  visualisationPlatform: boolean;
  customReports: boolean;
  scheduledReports: boolean;
  externalIntegrations: boolean;
  publicPortal: boolean;
  privatePortal: boolean;
  sso: boolean;
  auditLogExport: boolean;
  priorityJobQueue: boolean;
  multiRegion: boolean;
  premiumSupport: boolean;
};

export type PlanCatalogEntry = {
  id: string;
  name: string;
  tier: "starter" | "professional" | "enterprise" | "government";
  priceMonthlyUsd: number;
  seatLimit: number;
  datasetLimit: number;
  limits: PlanLimits;
  capabilities: PlanCapabilities;
  features: string[];
  description?: string;
  recommended?: boolean;
  waiveFees?: boolean;
  waiveFeesReason?: string;
  effectivePriceMonthlyUsd?: number;
};

export async function listPlans(): Promise<PlanCatalogEntry[]> {
  const res = await request<{ plans: PlanCatalogEntry[] }>("/api/plans", {}, false);
  return res.plans;
}

export async function signupTenant(input: {
  organizationName: string;
  workspaceSlug: string;
  planId: string;
  ownerName: string;
  ownerEmail: string;
  ownerUsername: string;
  ownerPassword: string;
  region?: string;
  dhis2BaseUrl?: string;
}): Promise<AuthSession> {
  const session = await request<AuthSession>(
    "/api/auth/signup",
    { method: "POST", body: JSON.stringify(input) },
    false
  );
  saveSession(session, session.displayName ?? session.username ?? input.ownerUsername);
  return session;
}

export async function loginWithDhis2Pat(input: {
  userId: string;
  personalAccessToken?: string;
  workspaceId?: string;
}): Promise<AuthSession> {
  const session = await request<AuthSession>(
    "/api/auth/token",
    { method: "POST", body: JSON.stringify({ ...input, authType: "dhis2" }) },
    false
  );
  saveSession(session, input.userId);
  return session;
}

/* ---------- API surface ---------- */
export const api = {
  // Overviews
  overview:          () => request<OverviewSnapshot>("/api/overview"),
  saasOverview:      () => request<SaaSOverview>("/api/saas/overview"),
  config:            () => request<{
    port?: number;
    dhis2BaseUrl?: string;
    authHeader: string;
    authType: string;
    workspaceId?: string;
    settings: Partial<AppSettings>;
    serviceHealth: Record<string, unknown>;
  }>("/api/config"),

  // Tenant workspace context (post-login)
  currentWorkspace:  () => request<WorkspaceContext>("/api/workspaces/current"),
  currentSubscription: () => request<{ subscription: SubscriptionRecord | null; plan: SubscriptionPackage | null; subscriptionActive: boolean }>("/api/workspaces/current/subscription"),
  workspaceDhisInstances: () => request<{
    instances: WorkspaceDhisInstance[];
    subscriptionActive: boolean;
    workspaceId: string;
    workspaceSlug: string | null;
  }>("/api/workspaces/current/dhis2-instances"),
  listWorkspaceMembers: () => request<{
    members: Array<{ id: string; name: string; email: string; username?: string; role: string; status: string; mfaEnabled: boolean; lastLoginAt?: string }>;
    seatLimit: number;
    seatsUsed: number;
  }>("/api/workspaces/current/members"),
  createWorkspaceMember: (body: {
    name: string;
    email: string;
    username: string;
    role: "admin" | "analyst" | "billing" | "support";
    password: string;
  }) => request<{ id: string; name: string; email: string; username?: string; role: string; status: string; mfaEnabled: boolean }>(
    "/api/workspaces/current/members",
    { method: "POST", body: JSON.stringify(body) }
  ),
  removeWorkspaceMember: (memberId: string) =>
    request<void>(`/api/workspaces/current/members/${encodeURIComponent(memberId)}`, { method: "DELETE" }),

  // Tenancy
  workspace:         () => request<{ workspaces: TenantWorkspace[]; members: WorkspaceMember[] }>("/api/saas/workspace"),
  subscription:      () => request<{ plans?: SubscriptionPackage[]; subscriptions: SubscriptionRecord[] }>("/api/saas/subscription"),
  cancelSubscription: (subscriptionId: string) =>
    request<null>(`/api/saas/subscription/${encodeURIComponent(subscriptionId)}/cancel`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  changeSubscriptionPlan: (subscriptionId: string, planId: string) =>
    request<{ subscription: SubscriptionRecord }>(
      `/api/saas/subscription/${encodeURIComponent(subscriptionId)}/change-plan`,
      { method: "POST", body: JSON.stringify({ planId }) }
    ),
  subscriptionPackages: () => request<{ packages: SubscriptionPackage[] }>("/api/saas/subscription/packages"),
  createSubscriptionPackage: (pkg: SubscriptionPackage) =>
    request<{ package: SubscriptionPackage }>("/api/saas/subscription/packages", {
      method: "POST",
      body: JSON.stringify(pkg)
    }),
  getSubscriptionPackage: (id: string) =>
    request<{ package: SubscriptionPackage }>(`/api/saas/subscription/packages/${encodeURIComponent(id)}`),
  updateSubscriptionPackage: (id: string, patch: Partial<SubscriptionPackage>) =>
    request<{ package: SubscriptionPackage }>(`/api/saas/subscription/packages/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(patch)
    }),
  updateSubscriptionPackageLimits: (id: string, body: { rateLimits?: Record<string, number>; features?: Record<string, boolean> }) =>
    request<{ package: SubscriptionPackage }>(`/api/saas/subscription/packages/${encodeURIComponent(id)}/limits`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
  deleteSubscriptionPackage: (id: string) =>
    request<{ ok: true }>(`/api/saas/subscription/packages/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // Admin plan catalog (SubscriptionPlan) — gated by platform.plans.manage
  recomputeTallies: (workspaceId?: string) =>
    request<{ job: { id: string; status: string; type: string } }>("/api/tallies/recompute", {
      method: "POST",
      body: JSON.stringify(workspaceId ? { workspaceId } : {})
    }),
  regenerateAllTallies: () =>
    request<{
      status: string;
      enqueued: number;
      jobs: Array<{ id: string; workspaceId: string; environment: string }>;
    }>("/api/tallies/regenerate-all", { method: "POST", body: "{}" }),
  dataPreview: (opts?: { workspaceId?: string; sample?: number }) => {
    const params = new URLSearchParams();
    if (opts?.workspaceId) params.set("workspaceId", opts.workspaceId);
    if (opts?.sample) params.set("sample", String(opts.sample));
    const qs = params.toString();
    return request<{
      workspaceId: string | null;
      counts: { trackerEvents: number; trackedEntities: number; submittedAggregateFacts: number };
      eventsByProgram: Array<{ programId: string; count: number }>;
      factsByDataSet: Array<{ dataSetId: string; count: number }>;
      sample: {
        trackerEvents: Array<{ id: string; programId: string; programStageId: string; orgUnitId: string; eventDate: string; status: string; dataValueCount: number; enrollmentId?: string; trackedEntityId?: string; lastUpdated: string; workspaceId?: string }>;
        trackedEntities: Array<{ id: string; trackedEntityType: string; orgUnitId: string; attributeCount: number; createdAt: string; lastUpdated: string; workspaceId?: string }>;
        submittedAggregateFacts: Array<{ id: string; dataElementId: string; dataSetId?: string; periodId: string; orgUnitId: string; value: number; lastUpdated: string; workspaceId?: string }>;
      };
      lastSync: {
        trackerEvents: SyncSummary;
        submittedAggregateFacts: SyncSummary;
      };
    }>(qs ? `/api/data-preview?${qs}` : "/api/data-preview");
  },
  listTrackerEvents: (opts?: { programId?: string; orgUnitId?: string }) => {
    const params = new URLSearchParams();
    if (opts?.programId) params.set("programId", opts.programId);
    if (opts?.orgUnitId) params.set("orgUnitId", opts.orgUnitId);
    const qs = params.toString();
    return request<{ events: Array<{
      id: string;
      programId: string;
      programStageId: string;
      orgUnitId: string;
      eventDate: string;
      status: string;
      enrollmentId?: string;
      trackedEntityId?: string;
      lastUpdated: string;
      workspaceId?: string;
      attributeOptionComboId?: string;
      dataValues?: Array<{ dataElement: string; value: string }>;
    }> }>(qs ? `/api/tracker/events?${qs}` : "/api/tracker/events");
  },
  listTrackedEntities: (opts?: { trackedEntityType?: string }) => {
    const params = new URLSearchParams();
    if (opts?.trackedEntityType) params.set("trackedEntityType", opts.trackedEntityType);
    const qs = params.toString();
    return request<{ trackedEntities: Array<{
      id: string;
      trackedEntityType: string;
      orgUnitId: string;
      attributes: Record<string, string>;
      createdAt: string;
      lastUpdated: string;
      workspaceId?: string;
    }> }>(qs ? `/api/tracker/tracked-entities?${qs}` : "/api/tracker/tracked-entities");
  },
  warehouseIndicators: (opts?: { workspaceId?: string; indicatorId?: string; periodIds?: string[]; orgUnitIds?: string[]; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.workspaceId) params.set("workspaceId", opts.workspaceId);
    if (opts?.indicatorId) params.set("indicatorId", opts.indicatorId);
    if (opts?.periodIds?.length) params.set("periodIds", opts.periodIds.join(","));
    if (opts?.orgUnitIds?.length) params.set("orgUnitIds", opts.orgUnitIds.join(","));
    if (opts?.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return request<{
      rows: Array<{
        workspaceId: string;
        indicatorId: string;
        numeratorMetricId: string;
        denominatorMetricId: string | null;
        periodId: string;
        orgUnitId: string;
        numerator: number;
        denominator: number;
        value: number;
        valid: boolean;
        computedAt: string;
      }>;
    }>(qs ? `/api/warehouse/indicators?${qs}` : "/api/warehouse/indicators");
  },
  bucketContributors: (opts: { metricId: string; periodId: string; orgUnitId: string; dimensionTuple?: string[]; workspaceId?: string; limit?: number }) => {
    const params = new URLSearchParams();
    params.set("metricId", opts.metricId);
    params.set("periodId", opts.periodId);
    params.set("orgUnitId", opts.orgUnitId);
    if (opts.dimensionTuple) params.set("dimensionTuple", opts.dimensionTuple.join("|"));
    if (opts.workspaceId) params.set("workspaceId", opts.workspaceId);
    if (opts.limit) params.set("limit", String(opts.limit));
    return request<{
      contributors: Array<{
        sourceRecordId: string;
        workspaceId?: string;
        contributions: Array<{
          delta: number;
          bucketKey: string;
          metricId: string;
          programId: string;
          stageId?: string;
          orgUnitId: string;
          periodId: string;
          dimensionTuple: string[];
          mutationId: string;
          metadataVersion: number;
        }>;
        trackerEvent?: {
          id: string;
          programId: string;
          programStageId: string;
          orgUnitId: string;
          eventDate: string;
          status: string;
          dataValues?: Array<{ dataElement: string; value: string }>;
          lastUpdated: string;
        };
        submittedAggregateFact?: {
          id: string;
          dataElementId: string;
          periodId: string;
          orgUnitId: string;
          dataSetId?: string;
          value: number;
          lastUpdated: string;
        };
      }>;
    }>(`/api/tally-buckets/contributors?${params.toString()}`);
  },
  listAggregateFacts: (opts?: { dataSetId?: string; periodId?: string; orgUnitId?: string; workspaceId?: string }) => {
    const params = new URLSearchParams();
    if (opts?.dataSetId) params.set("dataSetId", opts.dataSetId);
    if (opts?.periodId) params.set("periodId", opts.periodId);
    if (opts?.orgUnitId) params.set("orgUnitId", opts.orgUnitId);
    if (opts?.workspaceId) params.set("workspaceId", opts.workspaceId);
    const qs = params.toString();
    return request<{ facts: Array<{
      id: string;
      dataElementId: string;
      periodId: string;
      orgUnitId: string;
      categoryOptionComboId: string;
      attributeOptionComboId: string;
      dataSetId?: string;
      value: number;
      lastUpdated: string;
      lineageKey: string;
      auditSource: string;
      domainIds: string[];
      workspaceId?: string;
    }> }>(qs ? `/api/aggregate/facts?${qs}` : "/api/aggregate/facts");
  },
  listTallies: (opts?: { limit?: number; offset?: number; workspaceId?: string }) => {
    // Default to the server-side max (5000) so workspace + platform
    // consoles both load the whole reachable catalog in one round-trip.
    // Platform operators may pass workspaceId to drill into a single
    // tenant; tenant users have their workspace fixed by their session
    // and the param is ignored server-side.
    const params = new URLSearchParams();
    params.set("limit", String(opts?.limit ?? 5000));
    if (opts?.offset) params.set("offset", String(opts.offset));
    if (opts?.workspaceId) params.set("workspaceId", opts.workspaceId);
    return request<{
      tallies: Array<{
        id: string;
        programId: string;
        stageId?: string;
        kind: string;
        method?: "event" | "enrollment" | "tracked-entity" | "data-value" | "attribute-value" | "aggregate-fact";
        label: string;
        dataElementId?: string;
        optionValue?: string;
        workspaceId?: string;
        provenance?: string;
        metadataVersion?: number;
        variableName?: string;
        dimensions?: Array<{
          kind: "program" | "stage" | "data-element" | "option" | "attribute" | "attribute-option";
          id: string;
          name: string;
          optionSetId?: string;
          optionSetName?: string;
          programType?: "WITH_REGISTRATION" | "WITHOUT_REGISTRATION" | "aggregate";
        }>;
        attributeDimensionId?: string;
        attributeOptionId?: string;
        notAggregable?: boolean;
        notAggregableReason?: string;
        serviceProfileEnvironment?: string;
        dhis2InstanceUrl?: string;
        dhis2InstanceLabel?: string;
      }>;
      totalCount?: number;
      offset?: number;
      limit?: number;
    }>(`/api/tallies?${params.toString()}`);
  },
  listBuckets: () => request<{
    buckets: Array<{
      key: string;
      metricId: string;
      programId: string;
      stageId?: string;
      orgUnitId: string;
      periodId: string;
      dimensionTuple?: string[];
      value: number;
      workspaceId?: string;
    }>;
  }>("/api/mutations/buckets"),
  listIndicators: () => request<{
    indicators: Array<{
      id: string;
      name: string;
      numeratorMetricId: string;
      denominatorMetricId?: string;
      workspaceId?: string;
      published?: boolean;
    }>;
  }>("/api/indicators"),
  previewIndicator: (id: string) => request<{
    indicatorId: string;
    valid: boolean;
    value: number;
    diagnostics: string[];
  }>(`/api/indicators/${encodeURIComponent(id)}/preview`),
  listIndicatorMappings: () => request<{
    mappings: Array<{ id: string; indicatorId: string; workspaceId?: string }>;
  }>("/api/mappings"),
  getSmtpSettings: () => request<{
    source: "database" | "env-var-fallback" | "none";
    host: string; port: number; user: string;
    passwordSet: boolean;
    from: string; secure: boolean;
    updatedAt: string | null;
  }>("/api/platform/admin/smtp-settings"),
  setSmtpSettings: (body: { host: string; port: number; user: string; password?: string; from: string; secure: boolean }) =>
    request<{ ok: true; updatedAt: string }>("/api/platform/admin/smtp-settings", { method: "PUT", body: JSON.stringify(body) }),
  testSmtpSettings: (to: string) =>
    request<{ ok: boolean; sentTo?: string; error?: string }>("/api/platform/admin/smtp-settings/test", {
      method: "POST",
      body: JSON.stringify({ to })
    }),

  getNotificationSettings: () => request<{
    channels: { email?: { to?: string[]; digestHour?: number }; webhook?: { url?: string; secret?: string } };
    subscriptions: Record<string, { channels?: string[] }>;
    updatedAt: string | null;
  }>("/api/notifications/settings"),
  setNotificationSettings: (body: { channels: Record<string, unknown>; subscriptions: Record<string, unknown> }) =>
    request<{ ok: true }>("/api/notifications/settings", { method: "PUT", body: JSON.stringify(body) }),
  listCaptureForms: () => request<{
    forms: Array<{ id: string; workspaceId: string; name: string; description: string | null; status: string; domain: string; binding: Record<string, unknown>; fields: unknown[]; createdAt: string; updatedAt: string }>;
  }>("/api/capture/forms"),
  upsertCaptureForm: (form: { id?: string; name: string; description?: string; status?: string; domain: string; binding?: Record<string, unknown>; fields?: unknown[] }) =>
    request<{ id: string; ok: true }>("/api/capture/forms", { method: "POST", body: JSON.stringify(form) }),
  deleteCaptureForm: (id: string) =>
    request<{ ok: true }>(`/api/capture/forms/${encodeURIComponent(id)}`, { method: "DELETE" }),
  listCaptureSubmissions: (params?: { formId?: string; status?: string }) => {
    const qs = new URLSearchParams();
    if (params?.formId) qs.set("formId", params.formId);
    if (params?.status) qs.set("status", params.status);
    const suf = qs.toString() ? `?${qs.toString()}` : "";
    return request<{
      submissions: Array<{ id: string; workspaceId: string; formId: string; submitterUserId: string | null; status: string; values: Record<string, unknown>; notes: string | null; submittedAt: string; reviewedAt: string | null; reviewedByUserId: string | null }>;
    }>(`/api/capture/submissions${suf}`);
  },
  submitCapture: (form: { formId: string; values?: Record<string, unknown>; notes?: string }) =>
    request<{ id: string; ok: true }>("/api/capture/submissions", { method: "POST", body: JSON.stringify(form) }),
  reviewCapture: (id: string, status: string, notes?: string) =>
    request<{ ok: true }>(`/api/capture/submissions/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify({ status, notes })
    }),
  listWatermarks: () => request<{
    watermarks: Array<{ scope: string; key: string; lastUpdated: string; updatedAt?: string }>;
  }>("/api/sync/watermarks"),
  resetWatermark: (scope: string, key: string) =>
    request<{ ok: true }>(`/api/sync/watermarks/${encodeURIComponent(scope)}/${encodeURIComponent(key)}`, {
      method: "DELETE"
    }),
  warehouseSummary: (params?: { metricIds?: string[]; programIds?: string[]; periodIds?: string[]; orgUnitIds?: string[] }) => {
    const qs = new URLSearchParams();
    for (const k of ["metricIds", "programIds", "periodIds", "orgUnitIds"] as const) {
      const v = params?.[k];
      if (v && v.length) qs.set(k, v.join(","));
    }
    const suf = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ total: number }>(`/api/warehouse/summary${suf}`);
  },
  warehouseGrouped: (params?: { groupBy?: "metric" | "program" | "period" | "orgUnit"; metricIds?: string[]; programIds?: string[]; periodIds?: string[]; orgUnitIds?: string[] }) => {
    const qs = new URLSearchParams();
    if (params?.groupBy) qs.set("groupBy", params.groupBy);
    for (const k of ["metricIds", "programIds", "periodIds", "orgUnitIds"] as const) {
      const v = params?.[k];
      if (v && v.length) qs.set(k, v.join(","));
    }
    const suf = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ rows: Array<{ key: string; total: number }> }>(`/api/warehouse/grouped${suf}`);
  },
  warehouseTimeseries: (params?: { metricIds?: string[]; programIds?: string[]; orgUnitIds?: string[] }) => {
    const qs = new URLSearchParams();
    for (const k of ["metricIds", "programIds", "orgUnitIds"] as const) {
      const v = params?.[k];
      if (v && v.length) qs.set(k, v.join(","));
    }
    const suf = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ rows: Array<{ periodId: string; total: number }> }>(`/api/warehouse/timeseries${suf}`);
  },
  observabilityMetricsQuery: (params?: { metric?: string; bucket?: "minute" | "hour"; sinceMs?: number; jobType?: string }) => {
    const qs = new URLSearchParams();
    if (params?.metric) qs.set("metric", params.metric);
    if (params?.bucket) qs.set("bucket", params.bucket);
    if (params?.sinceMs) qs.set("since", String(params.sinceMs));
    if (params?.jobType) qs.set("jobType", params.jobType);
    const suf = qs.toString() ? `?${qs.toString()}` : "";
    return request<{
      metric: string; bucket: "minute" | "hour"; jobType: string | null; windowMs: number;
      points: Array<{ ts: string; value: number }>;
      summary: { totalSamples: number; min: number; max: number; sum: number };
    }>(`/api/observability/metrics/query${suf}`);
  },
  observabilitySummary: () => request<{
    checkedAt: string;
    windowHours: number;
    adapters: Record<string, boolean>;
    jobs: {
      total: number;
      completed: number;
      failed: number;
      queued: number;
      running: number;
      errorRate: number;
      latencyMs: { p50: number; p95: number; p99: number };
      byType: Record<string, { total: number; completed: number; failed: number }>;
    };
    forwarding: { totalRuns: number; failedRuns: number; failureRate: number };
  }>("/api/observability/summary"),

  // Transfer logs — unified data-movement timeline. workspaceId is
  // optional for platform admins (omit = global view). status / source
  // / since are server-side filters.
  listTransfers: (params?: {
    workspaceId?: string;
    status?: string;
    source?: string;
    sinceMs?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.workspaceId) qs.set("workspaceId", params.workspaceId);
    if (params?.status) qs.set("status", params.status);
    if (params?.source) qs.set("source", params.source);
    if (params?.sinceMs) qs.set("since", String(params.sinceMs));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<{
      transfers: Array<{
        id: string;
        source: "sync-job" | "forwarding-run";
        type: string;
        workspaceId?: string;
        environment?: string;
        destinationId?: string;
        startedAt: string;
        completedAt?: string;
        status: string;
        itemCount?: number;
        message?: string;
        error?: string;
        correlationId?: string;
        idempotencyKey?: string;
      }>;
      totalCount: number;
    }>(`/api/transfers${suffix}`);
  },
  getTransfer: (id: string) =>
    request<{
      source: "sync-job" | "forwarding-run";
      job?: { id: string; type: string; status: string; result?: unknown; error?: string };
      run?: { id: string; status: string; message?: string; itemsForwarded?: number };
    }>(`/api/transfers/${encodeURIComponent(id)}`),

  adminListPlans: () => request<{ plans: PlanCatalogEntry[] }>("/api/saas/plans"),
  adminCreatePlan: (plan: PlanCatalogEntry) =>
    request<{ plan: PlanCatalogEntry }>("/api/saas/plans", {
      method: "POST",
      body: JSON.stringify(plan)
    }),
  adminUpdatePlan: (planId: string, patch: Partial<PlanCatalogEntry>) =>
    request<{ plan: PlanCatalogEntry }>(`/api/saas/plans/${encodeURIComponent(planId)}`, {
      method: "PUT",
      body: JSON.stringify(patch)
    }),
  adminDeletePlan: (planId: string) =>
    request<{ ok: true }>(`/api/saas/plans/${encodeURIComponent(planId)}`, { method: "DELETE" }),
  workspacePlanDetail: (workspaceId: string) =>
    request<{
      workspace: TenantWorkspace;
      subscription: SubscriptionRecord | null;
      plan: SubscriptionPackage | null;
      package: SubscriptionPackage | null;
      subscriptionActive: boolean;
      limits: Record<string, number>;
      capabilities: Record<string, boolean>;
      usage: { seats: number; datasets: number; dhis2Instances: number; publicPortals: number };
    }>(`/api/saas/workspaces/${encodeURIComponent(workspaceId)}/plan`),
  paymentProviders: () =>
    request<{
      providers: Array<{
        name: string;
        displayName: string;
        description: string;
        configured: boolean;
        enabled: boolean;
        fields: Array<{
          key: string;
          label: string;
          secret?: boolean;
          required?: boolean;
          placeholder?: string;
          options?: readonly string[];
          present: boolean;
        }>;
        missingRequired: string[];
      }>;
    }>("/api/saas/payment-providers"),
  setPaymentProviderCredentials: (name: string, fields: Record<string, string>) =>
    request<{ provider: unknown }>(`/api/saas/payment-providers/${encodeURIComponent(name)}/credentials`, {
      method: "POST",
      body: JSON.stringify({ fields })
    }),
  clearPaymentProviderCredentials: (name: string) =>
    request<{ provider: unknown }>(`/api/saas/payment-providers/${encodeURIComponent(name)}/credentials`, {
      method: "DELETE"
    }),
  testPaymentProvider: (name: string) =>
    request<{ ok: boolean; message: string; missingRequired?: string[]; note?: string }>(
      `/api/saas/payment-providers/${encodeURIComponent(name)}/test`,
      { method: "POST", body: JSON.stringify({}) }
    ),
  togglePaymentProvider: (name: string, enable: boolean) =>
    request<{ provider: unknown }>(
      `/api/saas/payment-providers/${encodeURIComponent(name)}/${enable ? "enable" : "disable"}`,
      { method: "POST", body: JSON.stringify({}) }
    ),
  refundPayment: (body: { subscriptionId: string; providerName: string; reason?: string }) =>
    request<{ success: boolean; message?: string; manualActionRequired?: boolean }>(
      "/api/saas/billing/refund",
      {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify(body)
      }
    ),
  auditFiltered: (params: { actor?: string; action?: string; workspaceId?: string; from?: string; to?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params.actor) qs.set("actor", params.actor);
    if (params.action) qs.set("action", params.action);
    if (params.workspaceId) qs.set("workspaceId", params.workspaceId);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.limit != null) qs.set("limit", String(params.limit));
    const query = qs.toString();
    return request<{
      entries: Array<{
        id: string;
        action: string;
        actorRole: string;
        actorUserId?: string;
        workspaceId?: string;
        occurredAt: string;
        details: Record<string, unknown>;
      }>;
      totalMatching: number;
    }>(`/api/audit${query ? `?${query}` : ""}`);
  },
  downloadAuditCsv: (params: { actor?: string; action?: string; workspaceId?: string; from?: string; to?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.actor) qs.set("actor", params.actor);
    if (params.action) qs.set("action", params.action);
    if (params.workspaceId) qs.set("workspaceId", params.workspaceId);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.limit != null) qs.set("limit", String(params.limit));
    const query = qs.toString();
    const filename = `tallystream-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    return downloadCsv(`/api/audit.csv${query ? `?${query}` : ""}`, filename);
  },
  assignPackage: (body: { workspaceId: string; planId: string }) =>
    request<{ ok: true }>("/api/saas/subscription/assign-package", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  rbacRoles: () =>
    request<{
      roles: Array<{ key: string; name: string; description: string; scope: "platform" | "tenant" | "workspace"; isSystem: boolean; legacyAliases?: string[] }>;
      mapping: Record<string, string[]>;
    }>("/api/platform/admin/roles"),
  rbacPermissions: () =>
    request<{
      permissions: Array<{ key: string; name: string; description: string; scope: "platform" | "tenant" | "workspace"; category: string }>;
      platformPermissions: Array<{ key: string; name: string; description: string; scope: "platform"; category: string }>;
      workspacePermissions: Array<{ key: string; name: string; description: string; scope: "workspace"; category: string }>;
    }>("/api/platform/admin/permissions"),
  platformMe: () =>
    request<{
      userId?: string;
      role: string;
      workspaceId?: string;
      tenantId?: string;
      platformRoles: string[];
      workspaceRoles: Record<string, string[]>;
      permissions: string[];
    }>("/api/platform/admin/me"),
  workspaceMe: (workspaceId: string) =>
    request<{
      userId?: string;
      workspaceId: string;
      roles: string[];
      platformRoles: string[];
      permissions: string[];
    }>(`/api/workspaces/${encodeURIComponent(workspaceId)}/admin/me`),
  workspaceDashboard: (workspaceId: string) =>
    request<{
      workspace: { id: string; name: string; slug: string | null; status: string };
      subscription: { status: string; planId: string } | null;
      plan: { id: string; name: string } | null;
      limits: Record<string, number>;
      usage: { seats: number; dhis2Instances: number; publicPortals: number };
      connections: Array<{ environment: string; enabled: boolean; secretConfigured: boolean; lastSuccessfulConnection?: string }>;
      syncStatus: {
        lastTrackerMetadataSync: ReplayJob | null;
        lastTrackerDataSync: ReplayJob | null;
        lastAggregateMetadataSync: ReplayJob | null;
        lastAggregateDataSync: ReplayJob | null;
        trackerWatermarks: Array<{ scope: string; key: string; lastUpdated: string; updatedAt?: string }>;
        aggregateWatermarks: Array<{ scope: string; key: string; lastUpdated: string; updatedAt?: string }>;
        legacyGlobalTrackerWatermark: { scope: string; key: string; lastUpdated: string; updatedAt?: string } | null;
        watermarksCount: number;
      };
      portals: Array<{ id: string; slug: string; status: string; name: string }>;
      recentAudit: Array<{ id: string; action: string; occurredAt: string; actorRole?: string; actorUserId?: string; category?: string; severity?: string; details?: Record<string, unknown> }>;
    }>(`/api/workspaces/${encodeURIComponent(workspaceId)}/admin/dashboard`),
  workspaceAudit: (workspaceId: string, params?: { category?: string; severity?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.category) q.set("category", params.category);
    if (params?.severity) q.set("severity", params.severity);
    if (params?.limit != null) q.set("limit", String(params.limit));
    const qs = q.toString();
    return request<{
      entries: Array<{
        id: string;
        action: string;
        actorRole: string;
        actorUserId?: string;
        occurredAt: string;
        category?: string;
        severity?: string;
        details: Record<string, unknown>;
      }>;
      count: number;
    }>(`/api/workspaces/${encodeURIComponent(workspaceId)}/admin/audit${qs ? `?${qs}` : ""}`);
  },
  downloadWorkspaceAuditCsv: (workspaceId: string, params: { category?: string; severity?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.category) q.set("category", params.category);
    if (params.severity) q.set("severity", params.severity);
    if (params.limit != null) q.set("limit", String(params.limit));
    const qs = q.toString();
    const filename = `${workspaceId}-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    return downloadCsv(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/audit.csv${qs ? `?${qs}` : ""}`,
      filename
    );
  },
  workspaceSupport: (workspaceId: string) =>
    request<{
      workspaceName: string;
      status: string;
      seats: number;
      dhis2Connections: number;
      connectionsHealthy: number;
      onboardingChecklist: Array<{ key: string; label: string; done: boolean }>;
    }>(`/api/workspaces/${encodeURIComponent(workspaceId)}/admin/support`),
  workspaceForwarding: (workspaceId: string) =>
    request<{
      destinations: Array<{ id: string; label: string; kind: "dhis2" | "webhook" | "warehouse"; status: "active" | "paused" | "disabled"; target: string; updatedAt: string }>;
      schedules: Array<{ id: string; destinationId: string; label: string; cron: string; enabled: boolean }>;
      recentRuns: Array<{ id: string; destinationId: string; status: "pending" | "running" | "succeeded" | "failed"; occurredAt: string; finishedAt?: string; itemsForwarded?: number; message?: string }>;
    }>(`/api/workspaces/${encodeURIComponent(workspaceId)}/admin/forwarding`),
  createForwardingDestination: (
    workspaceId: string,
    body: { label: string; kind: "dhis2" | "webhook" | "warehouse"; target: string; status?: "active" | "paused" | "disabled"; config?: Record<string, unknown> }
  ) => request<{ destination: { id: string; workspaceId: string; label: string; kind: string; target: string; status: string; config?: Record<string, unknown>; createdAt: string; updatedAt: string } }>(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/forwarding/destinations`,
    { method: "POST", body: JSON.stringify(body) }
  ),
  updateForwardingDestination: (
    workspaceId: string,
    destinationId: string,
    patch: { label?: string; target?: string; status?: "active" | "paused" | "disabled"; config?: Record<string, unknown> }
  ) => request<{ destination: { id: string; workspaceId: string; label: string; kind: string; target: string; status: string; config?: Record<string, unknown>; updatedAt: string } }>(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/forwarding/destinations/${encodeURIComponent(destinationId)}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  ),
  deleteForwardingDestination: (workspaceId: string, destinationId: string) =>
    request<void>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/forwarding/destinations/${encodeURIComponent(destinationId)}`,
      { method: "DELETE" }
    ),
  createForwardingSchedule: (
    workspaceId: string,
    body: { destinationId: string; label: string; cron: string; enabled?: boolean }
  ) => request<{ schedule: { id: string; destinationId: string; label: string; cron: string; enabled: boolean } }>(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/forwarding/schedules`,
    { method: "POST", body: JSON.stringify(body) }
  ),
  deleteForwardingSchedule: (workspaceId: string, scheduleId: string) =>
    request<void>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/forwarding/schedules/${encodeURIComponent(scheduleId)}`,
      { method: "DELETE" }
    ),
  workspaceForwardingEnvelopes: (
    workspaceId: string,
    filter?: { destinationId?: string; status?: "pending" | "sending" | "delivered" | "failed" | "abandoned"; limit?: number }
  ) => {
    const params = new URLSearchParams();
    if (filter?.destinationId) params.set("destinationId", filter.destinationId);
    if (filter?.status) params.set("status", filter.status);
    if (filter?.limit) params.set("limit", String(filter.limit));
    const qs = params.toString();
    return request<{
      envelopes: Array<{
        id: string;
        workspaceId: string;
        destinationId: string;
        scheduleId?: string;
        idempotencyKey: string;
        kind: "dhis2" | "webhook" | "warehouse";
        payloadChecksum: string;
        itemCount: number;
        attemptCount: number;
        status: "pending" | "sending" | "delivered" | "failed" | "abandoned";
        lastError?: string;
        createdAt: string;
        updatedAt: string;
        sentAt?: string;
      }>;
    }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/forwarding/envelopes${qs ? `?${qs}` : ""}`
    );
  },
  retryForwardingEnvelope: (workspaceId: string, envelopeId: string) =>
    request<{ job: { id: string; type: string; status: string } }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/forwarding/envelopes/${encodeURIComponent(envelopeId)}/retry`,
      { method: "POST" }
    ),
  testForwardingDestination: (workspaceId: string, destinationId: string) =>
    request<{ job: { id: string; type: string; status: string } }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/forwarding/destinations/${encodeURIComponent(destinationId)}/test`,
      { method: "POST" }
    ),
  workspaceSecurity: (workspaceId: string) =>
    request<{
      mfaEnabledCount: number;
      memberCount: number;
      connectionsNeedingRotation: number;
      recentSecurityEvents: Array<{ id: string; action: string; actorRole: string; actorUserId?: string; occurredAt: string; category?: string; severity?: string; details: Record<string, unknown> }>;
    }>(`/api/workspaces/${encodeURIComponent(workspaceId)}/admin/security`),
  suspendTenant: (workspaceId: string, reason?: string) =>
    request<{ workspace: TenantWorkspace }>(
      `/api/platform/admin/tenants/${encodeURIComponent(workspaceId)}/suspend`,
      { method: "POST", body: JSON.stringify({ reason: reason ?? "" }) }
    ),
  restoreTenant: (workspaceId: string) =>
    request<{ workspace: TenantWorkspace }>(
      `/api/platform/admin/tenants/${encodeURIComponent(workspaceId)}/restore`,
      { method: "POST" }
    ),
  activateTenant: (workspaceId: string) =>
    request<{ workspace: TenantWorkspace }>(
      `/api/platform/admin/tenants/${encodeURIComponent(workspaceId)}/activate`,
      { method: "POST" }
    ),
  createTenant: (body: {
    organizationName: string;
    workspaceSlug: string;
    planId?: string;
    status?: "trial" | "active" | "restricted";
    region?: string;
    dhis2BaseUrl?: string;
    owner?: { name: string; email: string; username: string; password: string };
  }) =>
    request<{ workspace: TenantWorkspace; member?: WorkspaceMember }>(
      "/api/platform/admin/tenants",
      { method: "POST", body: JSON.stringify(body) }
    ),
  setWorkspaceMemberRole: (workspaceId: string, memberId: string, role: string) =>
    request<{ member: WorkspaceMember }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/members/${encodeURIComponent(memberId)}/role`,
      { method: "POST", body: JSON.stringify({ role }) }
    ),
  addTenantMember: (
    workspaceId: string,
    body: { name: string; email: string; username: string; role: "admin" | "analyst" | "billing" | "support"; password: string }
  ) =>
    request<{ id: string; name: string; email: string; username: string; role: string; status: string; mfaEnabled: boolean }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/members`,
      { method: "POST", body: JSON.stringify(body) }
    ),
  removeTenantMember: (workspaceId: string, memberId: string) =>
    request<null>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/members/${encodeURIComponent(memberId)}`,
      { method: "DELETE" }
    ),
  updateTenantSettings: (
    workspaceId: string,
    body: { workspaceName?: string; organizationName?: string; region?: string; dhis2BaseUrl?: string; supportTier?: "standard" | "priority" | "enterprise"; aggregateMode?: "tally" | "rollup" | "both" }
  ) =>
    request<{ workspace: { id: string; name: string; slug: string | null; status: string; tenantId: string | null; organizationName: string; region?: string; dhis2BaseUrl?: string; supportTier?: string } }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/settings`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),
  featureFlags: () =>
    request<{ flags: Array<{ key: string; enabled: boolean; description: string; updatedAt: string }> }>(
      "/api/platform/admin/feature-flags"
    ),
  updateFeatureFlag: (key: string, body: { enabled?: boolean; description?: string }) =>
    request<{ flag: { key: string; enabled: boolean; description: string; updatedAt: string } }>(
      `/api/platform/admin/feature-flags/${encodeURIComponent(key)}`,
      { method: "PUT", body: JSON.stringify(body) }
    ),
  platformWorkers: () =>
    request<{
      heartbeats: Array<{
        name: string;
        lastBeatAt: string;
        intervalMs?: number;
        detail?: Record<string, unknown>;
        ttlMs: number;
        ageMs: number;
        stale: boolean;
      }>;
      jobs: { queued: number; running: number; failed: number; deadLettered: number };
    }>("/api/platform/admin/workers"),
  platformHealth: () =>
    request<{
      adapters: Record<string, boolean | { status: string; info?: Record<string, unknown> }>;
      metrics: { requestsTotal: number; errorsTotal: number; authFailures: number; rateLimited: number };
      tenants: { total: number; active: number; trial: number; suspended: number; restricted: number };
      jobs: { total: number; queued: number; running: number; failed: number; deadLettered: number };
    }>("/api/platform/admin/health"),
  securityCenter: () =>
    request<{
      recentEvents: Array<{ id: string; action: string; actorRole: string; actorUserId?: string; workspaceId?: string; occurredAt: string; details: Record<string, unknown> }>;
      metrics: { authFailures: number; rateLimited: number };
      workspaces: Array<{ id: string; slug: string; status: string; suspendedAt?: string }>;
    }>("/api/platform/admin/security-center"),
  platformForwarding: () =>
    request<{
      summary: { destinationCount: number; activeDestinationCount: number; pausedDestinationCount: number; scheduleCount: number; recentFailedRuns: number };
      destinations: Array<{ id: string; workspaceId: string; workspaceName: string; label: string; kind: string; status: string; target: string; updatedAt: string; failedRunCount: number }>;
      recentRuns: Array<{ id: string; workspaceId: string; workspaceName: string; destinationId: string; status: string; startedAt: string; finishedAt?: string; itemsForwarded?: number; message?: string }>;
    }>("/api/platform/admin/forwarding"),
  platformConnections: () =>
    request<{
      summary: { total: number; healthy: number; disabled: number; missingSecret: number };
      connections: Array<{ environment: string; serviceName: string; baseUrl: string; enabled: boolean; authMode: string; secretConfigured: boolean; lastSuccessfulConnection?: string; workspaceId: string | null; workspaceName: string | null }>;
    }>("/api/platform/admin/connections"),
  supportConsole: () =>
    request<{
      workspaces: Array<{ id: string; slug: string; workspaceName: string; status: string; memberCount: number; lastLoginAt?: string }>;
    }>("/api/platform/admin/support-console"),
  userRoleAssignments: (params?: { userId?: string; workspaceId?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.userId) q.set("userId", params.userId);
    if (params?.workspaceId) q.set("workspaceId", params.workspaceId);
    if (params?.status) q.set("status", params.status);
    const qs = q.toString();
    return request<{
      assignments: Array<{
        id: string;
        userId: string;
        roleKey: string;
        scope: "platform" | "tenant" | "workspace";
        tenantId?: string;
        workspaceId?: string;
        assignedBy?: string;
        assignedAt: string;
        expiresAt?: string;
        revokedAt?: string;
        status: "active" | "revoked" | "expired";
      }>;
    }>(`/api/platform/admin/user-roles${qs ? `?${qs}` : ""}`);
  },
  grantUserRole: (body: { userId: string; roleKey: string; workspaceId?: string; tenantId?: string; expiresAt?: string }) =>
    request<{ assignment: { id: string; userId: string; roleKey: string; scope: string; status: string } }>(
      "/api/platform/admin/user-roles",
      { method: "POST", body: JSON.stringify(body) }
    ),
  revokeUserRole: (assignmentId: string) =>
    request<{ assignment: { id: string; status: string } }>(
      `/api/platform/admin/user-roles/${encodeURIComponent(assignmentId)}/revoke`,
      { method: "POST" }
    ),

  // Billing
  paymentMethods: () =>
    request<{ methods: string[] }>("/api/saas/billing/available-payment-methods"),
  initiatePayment: (body: { planId: string; workspaceId: string; paymentMethod: string }) =>
    request<PaymentIntentResponse>("/api/saas/billing/initiate-payment", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify(body)
    }),
  paymentStatus: (reference: string) =>
    request<Record<string, unknown>>(`/api/saas/billing/payment-status/${encodeURIComponent(reference)}`),

  // Data & portals
  datasets:      () => request<{ datasets: DatasetDefinition[] }>("/api/saas/datasets"),
  setDatasetStatus: (datasetId: string, action: "archive" | "activate") =>
    request<{ dataset: DatasetDefinition }>(
      `/api/saas/datasets/${encodeURIComponent(datasetId)}/${action}`,
      { method: "POST", body: JSON.stringify({}) }
    ),
  refreshDataset: (datasetId: string) =>
    request<{ dataset: DatasetDefinition }>(
      `/api/saas/datasets/${encodeURIComponent(datasetId)}/refresh`,
      { method: "POST", body: JSON.stringify({}) }
    ),
  deleteDataset: (datasetId: string) =>
    request<null>(`/api/saas/datasets/${encodeURIComponent(datasetId)}`, { method: "DELETE" }),
  publicPortal:  () => request<{ portals: PublicPortalSite[] }>("/api/saas/public-portal"),
  setPublicPortalStatus: (portalId: string, action: "publish" | "unpublish") =>
    request<{ portal: PublicPortalSite }>(
      `/api/saas/public-portal/${encodeURIComponent(portalId)}/${action}`,
      { method: "POST", body: JSON.stringify({}) }
    ),
  deletePublicPortal: (portalId: string) =>
    request<null>(`/api/saas/public-portal/${encodeURIComponent(portalId)}`, { method: "DELETE" }),
  security:      () => request<{ secrets: SecretStatus[] }>("/api/saas/security"),

  // Settings
  settings:      () => request<AppSettings>("/api/settings"),
  updateSettings: (next: AppSettings) =>
    request<AppSettings>("/api/settings", { method: "PUT", body: JSON.stringify(next) }),

  // Cross-tenant payment records (platform-admin only). Used by
  // /admin/monetisation/transactions + /invoices to render real provider
  // payment activity rather than a derived subscription-state snapshot.
  listAllPaymentRecords: (limit = 500) =>
    request<{ records: Array<{
      id: string;
      workspaceId: string;
      planId: string;
      providerName: string;
      providerReference?: string | null;
      status: string;
      amount?: number | null;
      currency?: string | null;
      initiatedAt?: string | null;
      completedAt?: string | null;
      metadata?: unknown;
    }> }>(`/api/admin/payments/records?limit=${limit}`),

  // Workspace-scoped human labels for DHIS2 ids — drives the
  // LabelsProvider so the SPA can render "Acme Health Centre" alongside
  // "OUFa1c…" instead of dumping bare UIDs into every table cell.
  labels: () =>
    request<{
      orgUnits: Record<string, string>;
      programs: Record<string, string>;
      programStages: Record<string, { name: string; programId?: string }>;
      dataElements: Record<string, string>;
      dataSets: Record<string, string>;
      categoryOptionCombos: Record<string, string>;
      attributeOptionCombos: Record<string, string>;
      trackedEntityAttributes: Record<string, string>;
      options: Record<string, string>;
      dimensions: { source: boolean; aggregateMetadataVersion: number; trackerMetadataVersion: number };
    }>("/api/admin/labels"),

  // Pipeline health — drives /admin/operations/pipeline-health.
  pipelineHealth: () =>
    request<{
      syncs: Array<{
        type: string;
        completed: number; failed: number; running: number; queued: number; deadLettered: number;
        latestCompletedAt: string | null;
      }>;
      watermarks: Array<{ scope: string; key: string; lastUpdated: string; updatedAt: string }>;
      rawRowCounts: Record<string, number>;
      generatedAt: string;
    }>("/api/admin/pipeline-health"),

  // Mail templates — drives /admin/operations/mail-templates.
  listMailTemplates: () =>
    request<{ templates: Array<{ id: string; label: string; description: string; sample: Record<string, unknown> }> }>(
      "/api/admin/mail-templates"
    ),
  previewMailTemplate: (id: string, vars?: Record<string, unknown>) => {
    const qs = vars ? `?vars=${encodeURIComponent(JSON.stringify(vars))}` : "";
    return request<{ id: string; label: string; subject: string; text: string; html?: string }>(
      `/api/admin/mail-templates/${encodeURIComponent(id)}/preview${qs}`
    );
  },
  sendTestMailTemplate: (id: string, to: string, vars?: Record<string, unknown>) =>
    request<{ ok: boolean; sentTo: string; transport: string; status: string }>(
      `/api/admin/mail-templates/${encodeURIComponent(id)}/test`,
      { method: "POST", body: JSON.stringify({ to, vars }) }
    ),

  // Per-workspace payment records (platform-admin view). Drives
  // /admin/workspaces/:id/billing for support drilldowns.
  listWorkspacePaymentRecords: (workspaceId: string) =>
    request<{ records: Array<{
      id: string;
      workspaceId: string;
      planId: string;
      providerName: string;
      providerReference?: string | null;
      status: string;
      amount?: number | null;
      currency?: string | null;
      initiatedAt?: string | null;
      completedAt?: string | null;
      metadata?: unknown;
    }> }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/payments`),
  testConnection: () =>
    request<Record<string, unknown>>("/api/settings/test-connection", { method: "POST", body: JSON.stringify({}) }),

  // Service profiles (DHIS2 instances)
  createServiceProfile: (profile: ServiceProfile) =>
    request<AppSettings>("/api/settings/service-profiles", {
      method: "POST",
      body: JSON.stringify(profile)
    }),
  updateServiceProfile: (environment: string, profile: ServiceProfile) =>
    request<AppSettings>(`/api/settings/service-profiles/${encodeURIComponent(environment)}`, {
      method: "PUT",
      body: JSON.stringify(profile)
    }),
  deleteServiceProfile: (environment: string) =>
    request<AppSettings>(`/api/settings/service-profiles/${encodeURIComponent(environment)}`, {
      method: "DELETE"
    }),
  setServiceProfileSecret: (environment: string, secretValue: string, kind: SecretStatus["kind"] = "dhis2-pat") =>
    request<AppSettings>(
      `/api/settings/service-profiles/${encodeURIComponent(environment)}/secret`,
      { method: "POST", body: JSON.stringify({ secretValue, kind }) }
    ),
  rotateServiceProfileSecret: (environment: string, secretValue: string, kind: SecretStatus["kind"] = "dhis2-pat") =>
    request<{ settings: AppSettings; rotated: boolean; rotatedAt: string }>(
      `/api/settings/service-profiles/${encodeURIComponent(environment)}/secret/rotate`,
      { method: "POST", body: JSON.stringify({ secretValue, kind }) }
    ),
  resyncDhis2Instance: (workspaceId: string, environment: string) =>
    request<{
      environment: string;
      cleared: {
        trackerEvents: number;
        trackerEnrollments: number;
        trackedEntities: number;
        submittedAggregateFacts: number;
        tallyDefinitions: number;
        tallyBuckets: number;
      };
      watermarksCleared: number;
      queuedJobIds: string[];
      startedAt: string;
    }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/admin/dhis2-connections/${encodeURIComponent(environment)}/resync`,
      { method: "POST", body: JSON.stringify({}) }
    ),
  verifyServiceProfileSecret: (environment: string) =>
    request<{
      ok: boolean;
      kind: string;
      rotatedAt: string;
      reachable?: boolean;
      authenticated?: boolean;
      version?: string;
      systemName?: string;
      message?: string;
    }>(
      `/api/settings/service-profiles/${encodeURIComponent(environment)}/secret/verify`,
      { method: "POST", body: JSON.stringify({}) }
    ),
  revokeServiceProfileSecret: (environment: string) =>
    request<AppSettings>(
      `/api/settings/service-profiles/${encodeURIComponent(environment)}/secret`,
      { method: "DELETE" }
    ),
  probeServiceProfile: (body: { baseUrl: string; authMode?: "none" | "token" | "basic"; secretValue?: string }) =>
    request<{
      ok: boolean;
      reachable: boolean;
      authenticated: boolean;
      apiVersion?: string;
      version?: string;
      revision?: string;
      contextPath?: string;
      systemId?: string;
      systemName?: string;
      healthEndpoint: string;
      message?: string;
    }>("/api/settings/service-profiles/probe", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  discoverServiceProfileAppClient: (environment: string) =>
    request<{
      app: { key: string; name: string; version?: string; launchUrl?: string };
      client: DhisAppClient;
      clientSecret: string;
      created: boolean;
    }>(
      `/api/settings/service-profiles/${encodeURIComponent(environment)}/discover-app-client`,
      { method: "POST", body: JSON.stringify({}) }
    ),

  // DHIS2 app clients (platform-issued credentials for embedded DHIS2 app)
  listAppClients: () => request<{ clients: DhisAppClient[] }>("/api/saas/app-clients"),
  createAppClient: (body: DhisAppClientCreateRequest) =>
    request<DhisAppClientCreateResponse>("/api/saas/app-clients", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  rotateAppClientSecret: (clientId: string) =>
    request<DhisAppClientCreateResponse>(
      `/api/saas/app-clients/${encodeURIComponent(clientId)}/rotate`,
      { method: "POST", body: JSON.stringify({}) }
    ),
  revokeAppClient: (clientId: string) =>
    request<{ client: DhisAppClient }>(
      `/api/saas/app-clients/${encodeURIComponent(clientId)}/revoke`,
      { method: "POST", body: JSON.stringify({}) }
    ),
  /**
   * v1.17.0 — queue a "run full backfill" command for the given app
   * client. The DHIS2 app picks it up on its next heartbeat (≤60s) and
   * runs `listener.backfill()` to push the entire historical metadata
   * + data corpus to the platform. Returns the queued command + the
   * full pending list (so the UI can show "1 backfill pending").
   */
  triggerAppClientBackfill: (clientId: string, note?: string) =>
    request<{
      command: { id: string; type: "backfill"; issuedAt: string; note?: string };
      pending: Array<{ id: string; type: "backfill"; issuedAt: string }>;
    }>(
      `/api/dhis-app/clients/${encodeURIComponent(clientId)}/commands`,
      { method: "POST", body: JSON.stringify({ type: "backfill", note }) }
    ),
  listAppClientPendingCommands: (clientId: string) =>
    request<{ pending: Array<{ id: string; type: "backfill"; issuedAt: string; note?: string }> }>(
      `/api/dhis-app/clients/${encodeURIComponent(clientId)}/commands`
    ),
  updateAppClient: (
    clientId: string,
    patch: Partial<Pick<DhisAppClient, "label" | "description" | "dhis2BaseUrl" | "allowedAuthorities" | "status">>
  ) =>
    request<{ client: DhisAppClient }>(`/api/saas/app-clients/${encodeURIComponent(clientId)}`, {
      method: "PUT",
      body: JSON.stringify(patch)
    }),
  deleteAppClient: (clientId: string) =>
    request<void>(`/api/saas/app-clients/${encodeURIComponent(clientId)}`, { method: "DELETE" }),

  // DHIS2 passthrough
  dhis2Me:      () => request<DhisMe>("/api/dhis2/me"),

  // Jobs
  jobs:         () => request<{ jobs: ReplayJob[] }>("/api/jobs"),
  retryJob:     (jobId: string) =>
    request<{ job: ReplayJob }>(`/api/jobs/${encodeURIComponent(jobId)}/retry`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  cancelJob:    (jobId: string) =>
    request<{ job: ReplayJob }>(`/api/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  requeueJob:   (jobId: string) =>
    request<{ job: ReplayJob }>(`/api/jobs/${encodeURIComponent(jobId)}/requeue`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  bulkRequeueDeadLettered: (filter?: { type?: string; olderThan?: string; jobIds?: string[] }) =>
    request<{ requeued: number; failed: number; jobIds: string[] }>(
      "/api/jobs/dead-letter/bulk-requeue",
      { method: "POST", body: JSON.stringify(filter ?? {}) }
    ),
  bulkDeleteDeadLettered: (filter?: { type?: string; olderThan?: string; jobIds?: string[] }) =>
    request<{ deleted: number; jobIds: string[] }>(
      "/api/jobs/dead-letter/bulk-delete",
      { method: "POST", body: JSON.stringify(filter ?? {}) }
    ),
  scheduledSync: () =>
    request<{
      scheduler: {
        lastTickAt: string | null;
        intervalMs: number | null;
        nextTickAt: string | null;
        stale: boolean | null;
        lastEnqueued: number | null;
        lastSkippedReason: string | null;
        lastError: string | null;
        includeMetadata: boolean | null;
        includeAggregateFacts: boolean | null;
        includeTracker: boolean | null;
      };
      targets: Array<{
        workspaceId: string;
        environment: string;
        serviceName: string;
        scheduledTypes: Array<{
          type:
            | "aggregate-metadata-sync"
            | "tracker-metadata-sync"
            | "submitted-aggregate-sync"
            | "tracker-events-sync"
            | "tally-recompute";
          lastEnqueuedAt: string | null;
          lastStatus: string | null;
          lastJobId: string | null;
        }>;
      }>;
    }>("/api/platform/admin/scheduled-sync"),
  triggerScheduledSync: (body: {
    type:
      | "aggregate-metadata-sync"
      | "tracker-metadata-sync"
      | "submitted-aggregate-sync"
      | "tracker-events-sync"
      | "tally-recompute";
    workspaceId: string;
    environment: string;
  }) =>
    request<{ job: ReplayJob }>("/api/platform/admin/scheduled-sync/trigger", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  replayJobs:   () => request<{ jobs: ReplayJob[] }>("/api/replay/jobs"),
  runReplay:    (body: Record<string, unknown>) =>
    request<{ job: ReplayJob }>("/api/replay/run", { method: "POST", body: JSON.stringify(body) }),

  // DHIS2 metadata snapshots — populated by aggregate-metadata-sync /
  // tracker-metadata-sync jobs. Snapshot shape mirrors the
  // AggregateMetadataSnapshot / TrackerMetadataSnapshot interfaces in
  // @tallystream/shared-types; we type-assert in the consuming page since
  // this package doesn't import the shared types.
  aggregateMetadata: (serviceProfileId?: string) =>
    request<{ snapshot: Record<string, unknown> | null }>(
      `/api/aggregate-metadata${serviceProfileId ? `?serviceProfileId=${encodeURIComponent(serviceProfileId)}` : ""}`
    ),
  trackerMetadata: () =>
    request<{ snapshot: Record<string, unknown> | null }>("/api/tracker-metadata"),
  // Per-workspace summary + section endpoints. The summary returns just
  // counts per entity type (~5KB) so the browser renders instantly; each
  // section's rows are pulled on-demand when the user expands it.
  aggregateMetadataSummary: (workspaceId?: string) =>
    request<{ summary: { id?: string; version?: number; createdAt?: string; workspaceId?: string | null; counts: Record<string, number> } | null }>(
      `/api/aggregate-metadata/summary${workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : ""}`
    ),
  trackerMetadataSummary: (workspaceId?: string) =>
    request<{ summary: { id?: string; version?: number; createdAt?: string; workspaceId?: string | null; counts: Record<string, number> } | null }>(
      `/api/tracker-metadata/summary${workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : ""}`
    ),
  aggregateMetadataSection: (section: string, workspaceId?: string) =>
    request<{ rows: Array<Record<string, unknown>> }>(
      `/api/aggregate-metadata/section/${encodeURIComponent(section)}${workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : ""}`
    ),
  trackerMetadataSection: (section: string, workspaceId?: string) =>
    request<{ rows: Array<Record<string, unknown>> }>(
      `/api/tracker-metadata/section/${encodeURIComponent(section)}${workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : ""}`
    ),
  aggregateMetadataWorkspaces: () =>
    request<{ workspaces: string[] }>("/api/aggregate-metadata/workspaces"),
  trackerMetadataWorkspaces: () =>
    request<{ workspaces: string[] }>("/api/tracker-metadata/workspaces"),
  triggerAggregateMetadataSync: (environment?: string) =>
    request<{ job: { id: string; status: string } }>("/api/aggregate-metadata/sync", {
      method: "POST",
      body: JSON.stringify(environment ? { environment } : {})
    }),
  triggerTrackerMetadataSync: (environment?: string) =>
    request<{ job: { id: string; status: string } }>("/api/tracker/metadata/sync", {
      method: "POST",
      body: JSON.stringify(environment ? { environment } : {})
    }),

  // Monitoring
  monitoring:   () => request<Record<string, unknown>>("/api/monitoring"),
  // (warehouse{Summary,Grouped,Timeseries} are defined earlier in this
  // file with richer parameter shapes — see lines ~830-860)
  // Aggregate Domain Engine (Studio)
  aggregateDomains: () => request<{
    domains: AggregateDomain[];
    versions: Array<{ id: string; domainId: string; version: number; createdAt: string; definitionSummary: string }>;
    mappings: Array<Record<string, unknown>>;
  }>("/api/aggregate-domains"),
  upsertAggregateDomain: (domain: AggregateDomain) =>
    request<{ domain: AggregateDomain }>("/api/aggregate-domains", {
      method: "POST",
      body: JSON.stringify(domain)
    }),
  activateAggregateDomain: (id: string, active: boolean) =>
    request<{ domain: AggregateDomain }>(`/api/aggregate-domains/${encodeURIComponent(id)}/activate`, {
      method: "POST",
      body: JSON.stringify({ active })
    }),
  archiveAggregateDomain: (id: string) =>
    request<{ domain: AggregateDomain }>(`/api/aggregate-domains/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  cloneAggregateDomain: (id: string, cloneId: string) =>
    request<{ domain: AggregateDomain }>(`/api/aggregate-domains/${encodeURIComponent(id)}/clone`, {
      method: "POST",
      body: JSON.stringify({ cloneId })
    }),
  aggregateDomainVersions: (id: string) =>
    request<{ versions: Array<{ id: string; domainId: string; version: number; createdAt: string; definitionSummary: string }> }>(
      `/api/aggregate-domains/${encodeURIComponent(id)}/versions`
    ),
  restateAggregateDomain: (id: string, mode: "incremental" | "full-rebuild" = "full-rebuild") =>
    request<{ run: AggregateRollupRun }>(
      `/api/aggregate-domains/${encodeURIComponent(id)}/restate`,
      { method: "POST", body: JSON.stringify({ mode }) }
    ),
  validateAggregateRollupIntegrity: (domainId?: string) =>
    request<{ report: AggregateRollupIntegrityReport }>(
      "/api/aggregate-rollup/integrity",
      { method: "POST", body: JSON.stringify(domainId ? { domainId } : {}) }
    ),

  aggregatePublicationProfiles: () =>
    request<{ profiles: AggregatePublicationProfile[] }>("/api/aggregate-publication/profiles"),
  upsertAggregatePublicationProfile: (profile: AggregatePublicationProfile) =>
    request<{ profile: AggregatePublicationProfile }>("/api/aggregate-publication/profiles", {
      method: "POST",
      body: JSON.stringify(profile)
    }),
  deleteAggregatePublicationProfile: (id: string) =>
    request<{ status: string }>(`/api/aggregate-publication/profiles/${encodeURIComponent(id)}`, { method: "DELETE" }),
  aggregatePublicationRuns: () =>
    request<{ runs: AggregatePublicationRun[]; items: AggregatePublicationItem[] }>("/api/aggregate-publication/runs"),
  previewAggregatePublication: (profileId: string) =>
    request<{ preview: AggregatePublicationPreview }>("/api/aggregate-publication/preview", {
      method: "POST",
      body: JSON.stringify({ profileId })
    }),
  runAggregatePublication: (profileId: string, opts: { dryRun?: boolean } = {}) =>
    request<{ run: AggregatePublicationRun; items: AggregatePublicationItem[] }>("/api/aggregate-publication/run", {
      method: "POST",
      body: JSON.stringify({ profileId, ...(opts.dryRun ? { dryRun: true } : {}) })
    }),
  replayAggregatePublication: (runId: string) =>
    request<{ run: AggregatePublicationRun; items: AggregatePublicationItem[] }>(
      `/api/aggregate-publication/runs/${encodeURIComponent(runId)}/replay`,
      { method: "POST", body: JSON.stringify({}) }
    ),

  aggregateAnalytics: () =>
    request<{
      rows: Array<{ domainId: string; domainName: string; periodId: string; orgUnitId: string; dataElementId: string; value: number; sourceType: "tracker" | "submitted-aggregate" | "hybrid" }>;
      comparisons: Array<{ domainId: string; periodId: string; orgUnitId: string; trackerValue: number; submittedValue: number; variance: number }>;
    }>("/api/aggregate-analytics"),
  downloadAggregateAnalyticsCsv: (queryString = "") => {
    const filename = `aggregate-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    return downloadCsv(`/api/aggregate-analytics.csv${queryString ? `?${queryString}` : ""}`, filename);
  },

  reconciliation: () =>
    request<{
      lastReconciliationAt?: string;
      driftCount: number;
      retryCount: number;
      failedMutations: number;
      recentWarnings: string[];
      warehouseStatus: "ready" | "lagging" | "stale";
    }>("/api/reconciliation"),
  aggregateReconciliation: () =>
    request<{
      latest: AggregateDomainReconciliationRun | null;
      comparisons: Array<{ domainId: string; periodId: string; orgUnitId: string; trackerValue: number; submittedValue: number; variance: number }>;
    }>("/api/aggregate-reconciliation"),
  runAggregateReconciliation: () =>
    request<AggregateDomainReconciliationRun>("/api/aggregate-reconciliation/run", {
      method: "POST",
      body: JSON.stringify({})
    }),
  runDhis2Reconciliation: (body: { scope: "aggregate" | "tracker"; environment?: string; dataSetId?: string; programId?: string; lastUpdatedAfter?: string; periodIds?: string[]; orgUnitIds?: string[] }) =>
    request<{ id: string; status: string }>("/api/reconciliation/dhis2/run", {
      method: "POST",
      body: JSON.stringify(body)
    }),

  audit:        () => request<{ events: { id: string; action: string; actorRole: string; actorUserId?: string; occurredAt: string; details: Record<string, unknown> }[] }>("/api/audit"),
  access:       () => request<Record<string, unknown>>("/api/access"),

  // Programs / metadata
  programs:     () => request<{ programs: { id: string; name: string; enabled?: boolean }[] }>("/api/programs"),
  metadata:     () => request<Record<string, unknown>>("/api/metadata"),
  request:      <T>(path: string, init?: RequestInit) => request<T>(path, init)
};
